import type { CSSProperties } from 'react';
import { Spinner } from '../spinner/spinner';
import {
    buttonBase,
    primaryVariant,
    blockVariant,
    tertiaryVariant,
    criticalVariant,
} from './button.css';

export interface ButtonProps {
    children?: string | string[];
    size?: 'inline' | 'slim' | 'medium' | 'large';
    /** `critical` is the tertiary-weight destructive style (e.g. Kill, Delete). */
    variant?: 'primary' | 'block' | 'tertiary' | 'critical';
    fullWidth?: boolean;
    disabled?: boolean;
    isLoading?: boolean;
    /** Native button type; defaults to `button` so it never submits by accident. */
    type?: 'button' | 'submit' | 'reset';
    testID?: string;
    onPress?: () => void | Promise<void>;
}

const sizeStyles: Record<NonNullable<ButtonProps['size']>, CSSProperties> = {
    inline: { minHeight: 30, padding: '6px 12px', borderRadius: 10, fontSize: 14 },
    slim: { minHeight: 36, padding: '6px 12px', borderRadius: 8, fontSize: 14 },
    // ≥44px tap target for the default size.
    medium: { minHeight: 48, padding: '12px 18px', borderRadius: 12, fontSize: 16 },
    large: { minHeight: 64, padding: '16px 24px', borderRadius: 16, fontSize: 18 },
};

const variantClass = {
    primary: primaryVariant,
    block: blockVariant,
    tertiary: tertiaryVariant,
    critical: criticalVariant,
};

export function Button({
    children,
    size = 'medium',
    variant = 'primary',
    fullWidth,
    disabled,
    isLoading,
    type = 'button',
    testID,
    onPress,
}: ButtonProps) {
    const style: CSSProperties = {
        ...sizeStyles[size],
        // `fit-content` (not `align-self: flex-start`) keeps the button
        // content-sized inside a stretching column while still letting a row's
        // `blockAlign` place it on the cross axis.
        width: fullWidth ? '100%' : 'fit-content',
    };

    return (
        <button
            type={type}
            className={`${buttonBase} ${variantClass[variant]}`}
            style={style}
            disabled={disabled || isLoading}
            aria-busy={isLoading}
            data-testid={testID}
            onClick={onPress}
        >
            {isLoading ? <Spinner size="small" tone="text-inverse" /> : children}
        </button>
    );
}
